import { classNames } from '@commenda/utils';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  Droppable,
  DroppableProvided,
  DropResult,
} from '@hello-pangea/dnd';
import { Table } from '@tanstack/react-table';
import React, { useCallback, useRef, useState } from 'react';
import { Button } from '../../Button/Button';
import { IconButton } from '../../IconButton';
import { Icons } from '../../Icons';
import { Popover, PopoverContent, PopoverTrigger } from '../../Popover/Popover';
import { Tooltip } from '../../Tooltip';
import { ColumnGroup, CustomColumnDef } from '../types';
import { getColumnId } from '../utils';
import { TableGlobalActionTrigger } from './TableGlobalActionTrigger';

type Props<TData, TValue> = {
  table: Table<TData>;
  groups: ColumnGroup<TData, TValue>[];
  onApply: (nextGroups: ColumnGroup<TData, TValue>[]) => void;
  onReset?: () => void;
  placeholder?: string;
  defaultVisibleColumns?: string[];
  onColumnVisibilityChange?: (visible: string[]) => void;
};

export function TableCustomizeColumns<TData, TValue>(
  props: Props<TData, TValue>
) {
  const {
    table,
    groups,
    onApply,
    onReset,
    placeholder = 'Customize',
    defaultVisibleColumns,
    onColumnVisibilityChange,
  } = props;

  const [open, setOpen] = useState(false);
  // Drag-and-drop state handled by @hello-pangea/dnd

  // Capture the initial state at first mount for true Reset behavior
  const initialGroupsRef = useRef<ColumnGroup<TData, TValue>[]>(
    groups.map((g) => ({ ...g, columns: [...g.columns] }))
  );
  const [draftGroups, setDraftGroups] = useState<ColumnGroup<TData, TValue>[]>(
    () => groups.map((g) => ({ ...g, columns: [...g.columns] }))
  );

  // List of column ids that this UI is allowed to manage (excludes utility columns like 'select')
  const managedColumnIds = React.useMemo(() => {
    return groups
      .flatMap((g) => g.columns)
      .map((c) => getColumnId(c as CustomColumnDef<TData, TValue>));
  }, [groups]);

  // Visibility map from table state
  const visibleMap = table.getState().columnVisibility;
  const initialVisibilityRef = useRef<Record<string, boolean>>({
    ...visibleMap,
  });
  const [draftVisibility, setDraftVisibility] = useState<
    Record<string, boolean>
  >(() => ({ ...visibleMap }));

  // Track visual drop position to show blue separator
  const [overIndexUnpinned, setOverIndexUnpinned] = useState<number | null>(
    null
  );
  const [overIndexHidden, setOverIndexHidden] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Drafts are seeded when the popover opens — never on render. A re-render of the
  // table (data refetch, poll) would otherwise discard in-progress edits.
  const onPopoverOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        setDraftGroups(groups.map((g) => ({ ...g, columns: [...g.columns] })));
        setDraftVisibility({ ...table.getState().columnVisibility });
      }
      setOpen(nextOpen);
    },
    [groups, table]
  );

  const toggleVisibility = useCallback((columnId: string) => {
    setDraftVisibility((prev) => ({ ...prev, [columnId]: !prev[columnId] }));
  }, []);

  const setAllVisible = useCallback(() => {
    const next: Record<string, boolean> = { ...draftVisibility };
    managedColumnIds.forEach((id) => {
      next[id] = true;
    });
    setDraftVisibility(next);
  }, [draftVisibility, managedColumnIds]);

  const handleReset = useCallback(() => {
    // Restore the very first groups order from when the component mounted
    setDraftGroups(
      initialGroupsRef.current.map((g) => ({ ...g, columns: [...g.columns] }))
    );
    // Restore initial visibility at mount unless a default is explicitly provided
    if (defaultVisibleColumns && defaultVisibleColumns.length) {
      const next: Record<string, boolean> = { ...draftVisibility };
      // Only modify visibility for columns managed by this UI
      managedColumnIds.forEach((id) => {
        next[id] = false;
      });
      defaultVisibleColumns.forEach((id) => {
        if (managedColumnIds.includes(id)) next[id] = true;
      });
      // Ensure pinned (sticky) columns are always visible after reset
      const pinnedIds = initialGroupsRef.current
        .filter((g) => !!g.sticky)
        .flatMap((g) => g.columns)
        .map((c) => getColumnId(c as CustomColumnDef<TData, TValue>));
      pinnedIds.forEach((id) => {
        if (managedColumnIds.includes(id)) next[id] = true;
      });
      setDraftVisibility(next);
    } else {
      const next: Record<string, boolean> = { ...initialVisibilityRef.current };
      // Ensure pinned (sticky) columns are always visible after reset
      const pinnedIds = initialGroupsRef.current
        .filter((g) => !!g.sticky)
        .flatMap((g) => g.columns)
        .map((c) => getColumnId(c as CustomColumnDef<TData, TValue>));
      pinnedIds.forEach((id) => {
        if (managedColumnIds.includes(id)) next[id] = true;
      });
      setDraftVisibility(next);
    }
    onReset?.();
  }, [defaultVisibleColumns, managedColumnIds, onReset]);

  // (legacy manual DnD handlers removed)

  const applyChanges = useCallback(() => {
    // Apply order
    onApply(draftGroups);
    // Apply visibility to table
    const nextVisibility: Record<string, boolean> = {
      ...table.getState().columnVisibility,
      ...draftVisibility,
    };
    // Force pinned (sticky) columns to visible when applying
    const pinnedIds = draftGroups
      .filter((g) => !!g.sticky)
      .flatMap((g) => g.columns)
      .map((c) => getColumnId(c as CustomColumnDef<TData, TValue>));
    pinnedIds.forEach((id) => {
      if (managedColumnIds.includes(id)) nextVisibility[id] = true;
    });
    table.setColumnVisibility(nextVisibility);
    const visibleList = Object.entries(draftVisibility)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .filter((k) => k !== 'select');
    onColumnVisibilityChange?.(visibleList);
    setOpen(false);
  }, [
    draftGroups,
    onApply,
    draftVisibility,
    table,
    onColumnVisibilityChange,
    managedColumnIds,
  ]);

  // DnD handler is defined below where derived lists are available

  // Get all columns from all groups for display
  const allColumns = draftGroups.flatMap((group) =>
    group.columns.map((col) => ({
      ...col,
      groupId: group.id,
      groupSticky: group.sticky,
    }))
  );

  // Separate visible and hidden columns
  const visibleColumns = allColumns.filter((col) => {
    const id = getColumnId(col as CustomColumnDef<TData, TValue>);
    return draftVisibility[id];
  });

  const hiddenColumns = allColumns.filter((col) => {
    const id = getColumnId(col as CustomColumnDef<TData, TValue>);
    // Do not list sticky (pinned) columns under Hidden or Unpinned
    return !col.groupSticky && !draftVisibility[id];
  });

  // Pinned (sticky) columns
  const pinnedColumns = allColumns.filter((col) => col.groupSticky);

  // Count visible columns (not used in UI currently)
  // const visibleCount = visibleColumns.length;

  const handleDndEnd = useCallback(
    (result: DropResult) => {
      const { source, destination } = result;
      if (!destination) return;

      const fromList = source.droppableId as 'unpinned' | 'hidden';
      const toList = destination.droppableId as 'unpinned' | 'hidden';

      const getColumnByListIndex = (
        list: 'unpinned' | 'hidden',
        index: number
      ):
        | (CustomColumnDef<TData, TValue> & {
            groupId: string;
            groupSticky?: boolean;
          })
        | undefined => {
        if (list === 'unpinned') {
          const cols = visibleColumns.filter((c) => !c.groupSticky);
          return cols[index] as unknown as CustomColumnDef<TData, TValue> & {
            groupId: string;
            groupSticky?: boolean;
          };
        }
        const cols = hiddenColumns;
        return cols[index] as unknown as CustomColumnDef<TData, TValue> & {
          groupId: string;
          groupSticky?: boolean;
        };
      };

      if (fromList !== toList) {
        const dragged = getColumnByListIndex(fromList, source.index);
        if (!dragged) return;
        const id = getColumnId(dragged as CustomColumnDef<TData, TValue>);
        const makeVisible = toList === 'unpinned';
        setDraftVisibility((prev) => ({ ...prev, [id]: makeVisible }));
        return;
      }

      if (fromList === 'unpinned' && toList === 'unpinned') {
        const dragged = getColumnByListIndex('unpinned', source.index);
        const target = getColumnByListIndex('unpinned', destination.index);
        if (!dragged || !target) return;

        const draggedId = getColumnId(
          dragged as CustomColumnDef<TData, TValue>
        );
        const targetId = getColumnId(target as CustomColumnDef<TData, TValue>);

        setDraftGroups((prev: ColumnGroup<TData, TValue>[]) => {
          const next = prev.map((g) => ({ ...g, columns: [...g.columns] }));
          const sourceGroupIndex = next.findIndex(
            (g) => g.id === (dragged as { groupId: string }).groupId
          );
          const targetGroupIndex = next.findIndex(
            (g) => g.id === (target as { groupId: string }).groupId
          );
          if (sourceGroupIndex === -1 || targetGroupIndex === -1) return prev;

          const sourceGroup = next[sourceGroupIndex];
          const targetGroup = next[targetGroupIndex];
          if (sourceGroup.sticky || targetGroup.sticky) return prev;

          if (sourceGroupIndex === targetGroupIndex) {
            const fromIdx = sourceGroup.columns.findIndex(
              (c) =>
                getColumnId(c as CustomColumnDef<TData, TValue>) === draggedId
            );
            const toIdx = targetGroup.columns.findIndex(
              (c) =>
                getColumnId(c as CustomColumnDef<TData, TValue>) === targetId
            );
            if (fromIdx === -1 || toIdx === -1) return prev;
            const cols = [...sourceGroup.columns];
            const [moved] = cols.splice(fromIdx, 1);
            const insertIndex =
              toIdx + (destination.index > source.index ? 1 : 0);
            cols.splice(insertIndex, 0, moved);
            next[sourceGroupIndex] = { ...sourceGroup, columns: cols };
            return next;
          }

          const movedColumn = sourceGroup.columns.find(
            (c) =>
              getColumnId(c as CustomColumnDef<TData, TValue>) === draggedId
          );
          if (!movedColumn) return prev;

          const newSourceGroup = {
            ...sourceGroup,
            columns: sourceGroup.columns.filter(
              (c) =>
                getColumnId(c as CustomColumnDef<TData, TValue>) !== draggedId
            ),
          };

          const targetIndexInGroup = targetGroup.columns.findIndex(
            (c) => getColumnId(c as CustomColumnDef<TData, TValue>) === targetId
          );
          const insertIndex =
            targetIndexInGroup + (destination.index > source.index ? 1 : 0);

          const newTargetGroup = {
            ...targetGroup,
            columns: [
              ...targetGroup.columns.slice(0, insertIndex),
              movedColumn,
              ...targetGroup.columns.slice(insertIndex),
            ],
          };

          const resultGroups = [...next];
          resultGroups[sourceGroupIndex] = newSourceGroup;
          resultGroups[targetGroupIndex] = newTargetGroup;
          return resultGroups;
        });
      }
    },
    [hiddenColumns, visibleColumns]
  );

  const filteredPinnedColumns = pinnedColumns?.filter(
    (pinnedColumn) => !pinnedColumn.id?.toLowerCase()?.includes('action')
  );

  return (
    <Popover open={open} onOpenChange={onPopoverOpenChange} modal>
      <PopoverTrigger asChild>
        <TableGlobalActionTrigger
          count={0}
          onClear={() => setAllVisible()}
          icon={<Icons.Sliders04 className="size-4" />}
          label={placeholder}
        />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className={classNames(
          'w-[264px] p-0 shadow-lg',
          isDragging ? 'overflow-visible' : 'overflow-clip'
        )}
      >
        <DragDropContext
          onDragStart={() => setIsDragging(true)}
          onDragUpdate={(update: {
            destination: { droppableId: string; index: number } | null;
          }) => {
            const dest = update.destination;
            if (!dest) {
              setOverIndexUnpinned(null);
              setOverIndexHidden(null);
              return;
            }
            if (dest.droppableId === 'unpinned') {
              setOverIndexUnpinned(dest.index);
              setOverIndexHidden(null);
            } else if (dest.droppableId === 'hidden') {
              setOverIndexHidden(dest.index);
              setOverIndexUnpinned(null);
            }
          }}
          onDragEnd={(result) => {
            setOverIndexUnpinned(null);
            setOverIndexHidden(null);
            setIsDragging(false);
            handleDndEnd(result);
          }}
        >
          <div className="flex flex-col max-h-[300px] overflow-y-auto">
            {/* Pinned columns section (non-draggable, no visibility toggle) */}
            {filteredPinnedColumns.length > 0 && (
              <div className="px-lg pt-lg pb-md border-b border-border-primary">
                <div className="py-xs pl-md txt-small-regular text-text-quaternary select-none">
                  Pinned
                </div>
                <div className="flex flex-col">
                  {filteredPinnedColumns.map((col) => {
                    const id = getColumnId(
                      col as CustomColumnDef<TData, TValue>
                    );
                    type MaybeTitled = { title?: string; header?: unknown };
                    const meta = col as unknown as MaybeTitled;
                    const label =
                      (typeof meta.header === 'string'
                        ? meta.header
                        : typeof meta.title === 'string'
                          ? meta.title
                          : undefined) || id;
                    return (
                      <div
                        key={id}
                        className="flex items-center justify-between p-xs gap-xs rounded-md transition-colors"
                      >
                        <div className="flex-1 p-xs txt-small-medium text-text-tertiary">
                          {String(label)}
                        </div>
                        <IconButton
                          size="sm"
                          icon={<Icons.Pin02 className="size-4" />}
                          aria-label="Drag to rearrange"
                          disabled
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div
              className={classNames(
                'flex flex-col gap-xs',
                filteredPinnedColumns.length > 0
                  ? 'py-md px-lg'
                  : 'px-lg pt-lg pb-md',
                hiddenColumns.length > 0 ? 'border-b border-border-primary' : ''
              )}
            >
              <div className="py-xs pl-md txt-small-regular text-text-quaternary select-none">
                Unpinned
              </div>
              <Droppable
                droppableId="unpinned"
                // Explicit clone to guarantee visibility during drag
                renderClone={(
                  cloneProvided: DraggableProvided,
                  _snapshot: unknown,
                  rubric: { source: { index: number } }
                ) => {
                  const col = visibleColumns.filter((c) => !c.groupSticky)[
                    rubric.source.index
                  ];
                  const id = getColumnId(col as CustomColumnDef<TData, TValue>);
                  type MaybeTitled = { title?: string; header?: unknown };
                  const meta = col as unknown as MaybeTitled;
                  const label =
                    (typeof meta.header === 'string'
                      ? meta.header
                      : typeof meta.title === 'string'
                        ? meta.title
                        : undefined) || id;
                  return (
                    <div
                      ref={cloneProvided.innerRef}
                      {...cloneProvided.draggableProps}
                      {...cloneProvided.dragHandleProps}
                      className="relative"
                    >
                      <div className="flex items-center justify-between p-xs gap-xs rounded-md bg-bg-primary shadow-lg ring-1 ring-border-primary z-[1000] cursor-grab">
                        <IconButton
                          size="xs"
                          icon={<Icons.Menu01 className="size-4" />}
                          aria-label="Drag to rearrange"
                          className="cursor-grab"
                        />
                        <div className="flex-1 p-xs txt-small-medium text-text-tertiary">
                          {String(label)}
                        </div>
                        <IconButton
                          size="sm"
                          icon={<Icons.Eye className="size-4" />}
                          aria-label="Hide column"
                          className="cursor-pointer"
                        />
                      </div>
                    </div>
                  );
                }}
              >
                {(provided: DroppableProvided) => (
                  <div
                    className="flex flex-col"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {visibleColumns
                      .filter((col) => !col.groupSticky)
                      .map((col, index) => {
                        const id = getColumnId(
                          col as CustomColumnDef<TData, TValue>
                        );
                        type MaybeTitled = { title?: string; header?: unknown };
                        const meta = col as unknown as MaybeTitled;
                        const label =
                          (typeof meta.header === 'string'
                            ? meta.header
                            : typeof meta.title === 'string'
                              ? meta.title
                              : undefined) || id;

                        const unpinnedCount = visibleColumns.filter(
                          (c) => !c.groupSticky
                        ).length;
                        const showTopBar = overIndexUnpinned === index;
                        const showBottomBar =
                          overIndexUnpinned === index + 1 ||
                          (overIndexUnpinned === unpinnedCount &&
                            index === unpinnedCount - 1);

                        return (
                          <Draggable key={id} draggableId={id} index={index}>
                            {(
                              dragProvided: DraggableProvided,
                              dragSnapshot
                            ) => (
                              <div
                                className="relative"
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                              >
                                {showTopBar && (
                                  <div className="absolute -top-0.5 left-0 right-0 h-[3px] bg-fg-brand z-10" />
                                )}
                                <div
                                  className={classNames(
                                    'flex items-center justify-between p-xs gap-xs rounded-md transition-colors cursor-grab',
                                    'hover:bg-bg-primary-hover',
                                    dragSnapshot.isDragging
                                      ? 'bg-bg-primary shadow-lg ring-1 ring-border-primary z-50'
                                      : 'overflow-clip'
                                  )}
                                >
                                  <Tooltip content="Drag to rearrange">
                                    <IconButton
                                      size="xs"
                                      icon={<Icons.Menu01 className="size-4" />}
                                      aria-label="Drag to rearrange"
                                      className="cursor-grab mt-1"
                                    />
                                  </Tooltip>
                                  <div className="flex-1 p-xs txt-small-medium text-text-tertiary">
                                    {String(label)}
                                  </div>
                                  <Tooltip content="Hide column">
                                    <IconButton
                                      size="sm"
                                      icon={<Icons.Eye className="size-4" />}
                                      aria-label="Hide column"
                                      className="cursor-pointer mt-0.5"
                                      onClick={() => toggleVisibility(id)}
                                    />
                                  </Tooltip>
                                </div>
                                {showBottomBar && (
                                  <div className="absolute -bottom-0.5 left-0 right-0 h-[3px] bg-fg-brand z-10" />
                                )}
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>

            {/* Hidden columns section */}
            {hiddenColumns.length > 0 && (
              <div className={classNames('flex flex-col gap-xs py-md px-lg')}>
                <div className="py-xs pl-md txt-small-regular text-text-quaternary select-none">
                  Hidden
                </div>
                <Droppable
                  droppableId="hidden"
                  renderClone={(
                    cloneProvided: DraggableProvided,
                    _snapshot: unknown,
                    rubric: { source: { index: number } }
                  ) => {
                    const col = hiddenColumns[rubric.source.index];
                    const id = getColumnId(
                      col as CustomColumnDef<TData, TValue>
                    );
                    type MaybeTitled = { title?: string; header?: unknown };
                    const meta = col as unknown as MaybeTitled;
                    const label =
                      (typeof meta.header === 'string'
                        ? meta.header
                        : typeof meta.title === 'string'
                          ? meta.title
                          : undefined) || id;
                    return (
                      <div
                        ref={cloneProvided.innerRef}
                        {...cloneProvided.draggableProps}
                        {...cloneProvided.dragHandleProps}
                        className="relative"
                      >
                        <div className="flex items-center justify-between p-xs gap-xs rounded-md bg-bg-primary shadow-lg ring-1 ring-border-primary z-[1000] cursor-grab">
                          <IconButton
                            size="sm"
                            icon={<Icons.Menu01 className="size-4" />}
                            aria-label="Drag to rearrange"
                            className="cursor-pointer mt-1"
                          />
                          <div className="flex-1 p-xs txt-small-medium text-text-tertiary">
                            {String(label)}
                          </div>
                          <IconButton
                            size="sm"
                            icon={<Icons.EyeOff className="size-4" />}
                            aria-label="Show column"
                            className="cursor-pointer mt-0.5"
                          />
                        </div>
                      </div>
                    );
                  }}
                >
                  {(provided: DroppableProvided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="flex flex-col"
                    >
                      {hiddenColumns.map((col, index) => {
                        const id = getColumnId(
                          col as CustomColumnDef<TData, TValue>
                        );
                        type MaybeTitled = { title?: string; header?: unknown };
                        const meta = col as unknown as MaybeTitled;
                        const label =
                          (typeof meta.header === 'string'
                            ? meta.header
                            : typeof meta.title === 'string'
                              ? meta.title
                              : undefined) || id;
                        const showTopBar = overIndexHidden === index;
                        const showBottomBar =
                          overIndexHidden === index + 1 ||
                          (overIndexHidden === hiddenColumns.length &&
                            index === hiddenColumns.length - 1);
                        return (
                          <Draggable key={id} draggableId={id} index={index}>
                            {(
                              dragProvided: DraggableProvided,
                              dragSnapshot
                            ) => (
                              <div
                                className="relative"
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                              >
                                {showTopBar && (
                                  <div className="absolute -top-0.5 left-0 right-0 h-[3px] bg-fg-brand z-10" />
                                )}
                                <div
                                  className={classNames(
                                    'flex items-center justify-between p-xs gap-xs rounded-md transition-colors cursor-grab',
                                    dragSnapshot.isDragging
                                      ? 'bg-bg-primary shadow-lg ring-1 ring-border-primary z-50'
                                      : ''
                                  )}
                                >
                                  <Tooltip content="Drag to rearrange">
                                    <IconButton
                                      size="xs"
                                      icon={<Icons.Menu01 className="size-4" />}
                                      aria-label="Drag to rearrange"
                                      className="cursor-grab mt-1"
                                    />
                                  </Tooltip>
                                  <div className="flex-1 p-xs txt-small-medium text-text-tertiary">
                                    {String(label)}
                                  </div>
                                  <Tooltip content="Show column">
                                    <IconButton
                                      size="sm"
                                      icon={<Icons.EyeOff className="size-4" />}
                                      aria-label="Show column"
                                      className="cursor-pointer mt-0.5"
                                      onClick={() => toggleVisibility(id)}
                                    />
                                  </Tooltip>
                                </div>
                                {showBottomBar && (
                                  <div className="absolute -bottom-0.5 left-0 right-0 h-[3px] bg-fg-brand z-10" />
                                )}
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            )}
          </div>
        </DragDropContext>

        <div className="border-t p-lg flex items-center justify-between gap-lg bg-bg-primary">
          <Button variant="secondary" size="sm" onClick={handleReset} fullWidth>
            Reset
          </Button>
          <Button variant="primary" size="sm" onClick={applyChanges} fullWidth>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
